import { AfterViewInit, Component, OnInit } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { debounceTime } from 'rxjs';
import moment from 'moment';
import { exportTablePdf } from 'src/app/core/utils/export-table-pdf';
import { AppExportBase64 } from 'src/app/services/ExportBase64/exportBase64.service';
import { EstoqueService } from 'src/app/services/EstoqueService/estoque.service';
import {
  IEstoqueRelatorioFilter,
  IEstoqueRelatorioPosicaoItem
} from 'src/app/services/EstoqueService/estoque.model';
import { IProduto } from 'src/app/services/ProdutoService/produto.model';
import { ProdutoService } from 'src/app/services/ProdutoService/produto.service';

type IOptions = { name: string; value: number | null };

@Component({
  selector: 'app-relatorio-estoque-posicao',
  templateUrl: './relatorio-estoque-posicao.component.html',
  styleUrls: ['./relatorio-estoque-posicao.component.scss']
})
export class RelatorioEstoquePosicaoComponent implements OnInit, AfterViewInit {
  form = this.fb.group({
    DT_INIC: [null as string | null],
    DT_FIM: [null as string | null],
    FK_PROD: [0]
  });

  isLoading = false;
  tableData: Array<any> = [];
  produtoOptions: IOptions[] = [{ name: 'Todos', value: 0 }];

  totais = {
    qtInic: 0,
    vlInic: 0,
    qtSaid: 0,
    vlSaid: 0,
    qtAtual: 0,
    vlAtual: 0
  };

  tableColumns = [
    { header: 'Produto', key: 'produto', type: 'text' },
    { header: 'Estoque Inicial (Qtd)', key: 'qtInic', type: 'text' },
    { header: 'Estoque Inicial (R$)', key: 'vlInic', type: 'text' },
    { header: 'Saídas (Qtd)', key: 'qtSaid', type: 'text' },
    { header: 'Saídas (R$)', key: 'vlSaid', type: 'text' },
    { header: 'Saldo Atual (Qtd)', key: 'qtAtual', type: 'text' },
    { header: 'Saldo Atual (R$)', key: 'vlAtual', type: 'text' }
  ];

  constructor(
    private fb: FormBuilder,
    private estoqueService: EstoqueService,
    private produtoService: ProdutoService,
    private appExportBase64: AppExportBase64
  ) {
    const fim = moment().endOf('day');
    const inicio = moment().startOf('month');
    this.form.patchValue({
      DT_INIC: inicio.format('YYYY-MM-DDTHH:mm'),
      DT_FIM: fim.format('YYYY-MM-DDTHH:mm')
    });
  }

  ngOnInit(): void {
    this.loadProdutos();
    this.loadData();
  }

  ngAfterViewInit(): void {
    this.form.valueChanges.pipe(debounceTime(300)).subscribe(() => this.loadData());
  }

  loadProdutos(): void {
    this.produtoService.GetProdutos().subscribe({
      next: (produtos: IProduto[]) => {
        this.produtoOptions = [
          { name: 'Todos', value: 0 },
          ...produtos.map(p => ({ name: p.NM_PROD, value: p.PK_PROD }))
        ];
      }
    });
  }

  onSelectChange(event: number | null, name: 'FK_PROD'): void {
    this.form.controls[name].setValue(event as any);
  }

  private createFilter(): IEstoqueRelatorioFilter {
    const filter: IEstoqueRelatorioFilter = {
      DT_INIC: this.form.controls.DT_INIC.value ?? undefined,
      DT_FIM: this.form.controls.DT_FIM.value ?? undefined
    };
    const fkProd = Number(this.form.controls.FK_PROD.value);
    if (fkProd > 0) filter.FK_PROD = fkProd;
    return filter;
  }

  private formatMoney(value: number): string {
    return Number(value).toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    });
  }

  loadData(): void {
    const filter = this.createFilter();
    if (!filter.DT_INIC || !filter.DT_FIM) return;

    this.isLoading = true;
    this.estoqueService.GetRelatorioPosicao(filter).subscribe({
      next: (items: IEstoqueRelatorioPosicaoItem[]) => {
        this.totais = {
          qtInic: items.reduce((acc, i) => acc + Number(i.QT_SALD_INIC), 0),
          vlInic: items.reduce((acc, i) => acc + Number(i.VL_SALD_INIC), 0),
          qtSaid: items.reduce((acc, i) => acc + Number(i.QT_SAID), 0),
          vlSaid: items.reduce((acc, i) => acc + Number(i.VL_SAID), 0),
          qtAtual: items.reduce((acc, i) => acc + Number(i.QT_SALD_ATUAL), 0),
          vlAtual: items.reduce((acc, i) => acc + Number(i.VL_SALD_ATUAL), 0)
        };

        this.tableData = items.map(item => ({
          produto: item.NM_PROD,
          qtInic: item.QT_SALD_INIC,
          vlInic: this.formatMoney(item.VL_SALD_INIC),
          qtSaid: item.QT_SAID,
          vlSaid: this.formatMoney(item.VL_SAID),
          qtAtual: item.QT_SALD_ATUAL,
          vlAtual: this.formatMoney(item.VL_SALD_ATUAL)
        }));
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
      }
    });
  }

  exportarExcel(): void {
    const filter = this.createFilter();
    if (!filter.DT_INIC || !filter.DT_FIM) return;

    this.isLoading = true;
    this.estoqueService
      .GetReportExcel({
        ReportType: 'posicao',
        DT_INIC: filter.DT_INIC,
        DT_FIM: filter.DT_FIM,
        FK_PROD: filter.FK_PROD
      })
      .subscribe({
        next: response => {
          this.appExportBase64.downloadExcel(response.ReportBase64, 'posicao-estoque');
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
        }
      });
  }

  exportarPdf(): void {
    const totais = this.totaisFormatados;
    exportTablePdf(
      'Relatório de Posição de Estoque',
      [
        { header: 'Produto', key: 'produto', width: 160 },
        { header: 'Inicial (Qtd)', key: 'qtInic', width: 70 },
        { header: 'Inicial (R$)', key: 'vlInic', width: 90 },
        { header: 'Saídas (Qtd)', key: 'qtSaid', width: 70 },
        { header: 'Saídas (R$)', key: 'vlSaid', width: 90 },
        { header: 'Atual (Qtd)', key: 'qtAtual', width: 70 },
        { header: 'Atual (R$)', key: 'vlAtual', width: 90 }
      ],
      this.tableData,
      'posicao-estoque',
      `Inicial: ${totais.qtInic} / ${totais.vlInic} | Saídas: ${totais.qtSaid} / ${totais.vlSaid} | Atual: ${totais.qtAtual} / ${totais.vlAtual}`
    );
  }

  get totaisFormatados() {
    return {
      qtInic: this.totais.qtInic,
      vlInic: this.formatMoney(this.totais.vlInic),
      qtSaid: this.totais.qtSaid,
      vlSaid: this.formatMoney(this.totais.vlSaid),
      qtAtual: this.totais.qtAtual,
      vlAtual: this.formatMoney(this.totais.vlAtual)
    };
  }
}
